import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ChevronLeft, Upload, Camera, CheckCircle, Clock, AlertCircle, X, FileText, Shield, ExternalLink, ArrowRight, Car
} from 'lucide-react';
import { toast } from 'sonner';

const VerificationPage = () => {
  const { user, api, refreshUser, legalCompliance, needsLegalReacceptance, refreshLegalCompliance, requiresPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  
  // GUEST CHECKOUT: Detect if user came from guest checkout flow
  const isGuestCheckout = location.state?.isGuestCheckout || requiresPasswordReset?.();
  
  // BOOKING INTENT PRESERVATION: Read and store return URL
  const returnUrlFromState = location.state?.from;
  
  // Store return URL in sessionStorage (survives refresh)
  useEffect(() => {
    if (returnUrlFromState) {
      sessionStorage.setItem('postVerificationReturnUrl', returnUrlFromState);
    }
  }, [returnUrlFromState]);
  
  // Get return URL (prefer state, fallback to sessionStorage)
  const getReturnUrl = () => {
    return returnUrlFromState || sessionStorage.getItem('postVerificationReturnUrl') || null;
  };
  
  // Extract vehicle name from URL for display (e.g., /vehicle/mazda-cx-5-2023-brisbane)
  const getVehicleNameFromUrl = (url) => {
    if (!url) return null;
    const match = url.match(/\/vehicle\/([^/?]+)/);
    if (match) {
      // Convert slug to readable name: mazda-cx-5-2023-brisbane → Mazda CX-5 2023
      const slug = match[1];
      const parts = slug.split('-');
      // Capitalize first letter of each part, keep numbers as-is
      return parts.slice(0, -1).map(p => 
        /^\d+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)
      ).join(' ');
    }
    return null;
  };
  
  // P0 CRITICAL: Legal acceptance state
  const [termsContent, setTermsContent] = useState(null);
  const [privacyContent, setPrivacyContent] = useState(null);
  const [legalLoading, setLegalLoading] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptingLegal, setAcceptingLegal] = useState(false);
  const [showTermsContent, setShowTermsContent] = useState(false);
  const [showPrivacyContent, setShowPrivacyContent] = useState(false);

  // P0 FIX: Legal acceptance state must ONLY be based on actual acceptance records
  // The fallback to check user fields was causing false positives
  // Now we ONLY trust legalCompliance.is_compliant from the backend
  const hasAcceptedLegal = legalCompliance?.is_compliant === true;

  useEffect(() => {
    fetchVerification();
    fetchLegalContent();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // P0: Fetch legal content for display
  const fetchLegalContent = async () => {
    try {
      const [termsRes, privacyRes] = await Promise.all([
        api.get('/legal/terms'),
        api.get('/legal/privacy')
      ]);
      setTermsContent(termsRes.data);
      setPrivacyContent(privacyRes.data);
    } catch (error) {
      console.error('Error fetching legal content:', error);
    } finally {
      setLegalLoading(false);
    }
  };

  // P0: Handle legal acceptance
  const handleAcceptLegal = async () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      toast.error('Please check both boxes to accept Terms and Privacy Policy');
      return;
    }

    setAcceptingLegal(true);
    try {
      await api.post('/users/accept-legal', {
        terms_version_id: termsContent?.version_id,
        privacy_version_id: privacyContent?.version_id
      });
      
      toast.success('Terms & Privacy Policy accepted!');
      await refreshLegalCompliance();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept legal documents');
    } finally {
      setAcceptingLegal(false);
    }
  };

  const fetchVerification = async () => {
    try {
      const response = await api.get('/users/verification/status');
      setVerification(response.data);
      
      // P0 HOTFIX: If status was fixed on the backend, refresh user context
      if (response.data.status_mismatch_fixed || response.data.status !== user?.verification_status) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error fetching verification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) return;
    
    setUploading({ ...uploading, [type]: true });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', type);

    try {
      await api.post('/users/verification/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${type.replace('_', ' ')} uploaded successfully`);
      // P0 FIX: Await both calls so AuthContext user is guaranteed fresh
      // before "Return to Vehicle" button can appear
      await Promise.all([
        fetchVerification(),
        refreshUser()
      ]);
    } catch (error) {
      toast.error(`Failed to upload ${type.replace('_', ' ')}`);
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      setStream(mediaStream);
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      toast.error('Unable to access camera. Please upload a selfie instead.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      await handleFileUpload('selfie', file);
      stopCamera();
    }, 'image/jpeg', 0.8);
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const getStatusBadge = (status) => {
    if (status === 'verified') {
      return <Badge className="bg-green-100 text-green-800 gap-1"><CheckCircle className="w-3 h-3" />Verified</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-800 gap-1"><Clock className="w-3 h-3" />Pending Review</Badge>;
    }
    if (status === 'rejected') {
      return <Badge className="bg-red-100 text-red-800 gap-1"><AlertCircle className="w-3 h-3" />Rejected</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-800 gap-1"><AlertCircle className="w-3 h-3" />Not Submitted</Badge>;
  };

  const documents = [
    { key: 'licence_front', label: "Driver's Licence (Front)", icon: Upload },
    { key: 'licence_back', label: "Driver's Licence (Back)", icon: Upload },
    { key: 'selfie', label: 'Selfie Photo', icon: Camera }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004B8D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-semibold">Identity Verification</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 md:px-8 py-6 md:py-8 space-y-5">
        {/* GUEST CHECKOUT: Password setup reminder */}
        {isGuestCheckout && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Don't forget to set your password!</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Check your email for a link to set your password. This will secure your account after you complete verification.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Status */}
        <Card className="border-slate-100">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold">Verification Status</h3>
                <p className="text-sm text-slate-500">Complete all steps to get verified</p>
              </div>
              {getStatusBadge(verification?.status)}
            </div>
          </CardContent>
        </Card>

        {/* BOOKING INTENT: Return to vehicle after verification */}
        {getReturnUrl() && (verification?.status === 'verified' || verification?.status === 'pending') && (
          <Card className={`border-2 ${verification?.status === 'verified' ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${verification?.status === 'verified' ? 'bg-green-100' : 'bg-blue-100'}`}>
                  <Car className={`w-5 h-5 ${verification?.status === 'verified' ? 'text-green-600' : 'text-blue-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  {verification?.status === 'verified' ? (
                    <>
                      <h4 className="font-semibold text-green-800 mb-1">You're verified! Ready to book.</h4>
                      <p className="text-sm text-green-700 mb-3">
                        {getVehicleNameFromUrl(getReturnUrl()) 
                          ? `Return to ${getVehicleNameFromUrl(getReturnUrl())} to complete your booking.`
                          : 'Return to complete your booking.'}
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 className="font-semibold text-blue-800 mb-1">Verification in progress</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        We'll email you once approved. You can still view the vehicle while you wait.
                      </p>
                    </>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => {
                        const url = getReturnUrl();
                        sessionStorage.removeItem('postVerificationReturnUrl');
                        navigate(url);
                      }}
                      className={`gap-2 ${verification?.status === 'verified' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                      data-testid="return-to-booking-btn"
                    >
                      {getVehicleNameFromUrl(getReturnUrl()) 
                        ? `Return to ${getVehicleNameFromUrl(getReturnUrl())}`
                        : 'Return to your booking'}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/')}
                      className="text-slate-600"
                    >
                      Browse other vehicles
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* P0 CRITICAL: Step 1 - Legal Acceptance */}
        <Card className={`border-2 ${hasAcceptedLegal ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${hasAcceptedLegal ? 'bg-green-500' : 'bg-amber-500'}`}>
                {hasAcceptedLegal ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <div>
                <CardTitle className="text-lg">Terms & Privacy Policy</CardTitle>
                <CardDescription>
                  {hasAcceptedLegal ? 'You have accepted our terms' : 'Please review and accept to continue'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {hasAcceptedLegal ? (
              <div className="flex items-center gap-2 text-green-700 bg-green-100 px-4 py-3 rounded-lg">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Terms & Privacy Policy accepted</span>
              </div>
            ) : legalLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Terms of Service */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setShowTermsContent(!showTermsContent)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#004B8D]" />
                      <span className="font-medium">Terms of Service</span>
                      {termsContent?.version_id && (
                        <Badge variant="outline" className="text-xs">v{termsContent.version_id}</Badge>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </button>
                  {showTermsContent && termsContent?.content && (
                    <div className="max-h-48 overflow-y-auto p-4 bg-white border-t border-slate-200 text-sm text-slate-600">
                      <div dangerouslySetInnerHTML={{ __html: termsContent.content }} />
                    </div>
                  )}
                </div>

                {/* Privacy Policy */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setShowPrivacyContent(!showPrivacyContent)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-[#004B8D]" />
                      <span className="font-medium">Privacy Policy</span>
                      {privacyContent?.version_id && (
                        <Badge variant="outline" className="text-xs">v{privacyContent.version_id}</Badge>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </button>
                  {showPrivacyContent && privacyContent?.content && (
                    <div className="max-h-48 overflow-y-auto p-4 bg-white border-t border-slate-200 text-sm text-slate-600">
                      <div dangerouslySetInnerHTML={{ __html: privacyContent.content }} />
                    </div>
                  )}
                </div>

                {/* Checkboxes */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="accept-terms" 
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                      className="mt-0.5"
                      data-testid="accept-terms-checkbox"
                    />
                    <label htmlFor="accept-terms" className="text-sm cursor-pointer">
                      I have read and agree to the <strong>Terms of Service</strong>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="accept-privacy" 
                      checked={acceptedPrivacy}
                      onCheckedChange={setAcceptedPrivacy}
                      className="mt-0.5"
                      data-testid="accept-privacy-checkbox"
                    />
                    <label htmlFor="accept-privacy" className="text-sm cursor-pointer">
                      I have read and agree to the <strong>Privacy Policy</strong>
                    </label>
                  </div>
                </div>

                {/* Accept Button */}
                <Button 
                  onClick={handleAcceptLegal}
                  disabled={!acceptedTerms || !acceptedPrivacy || acceptingLegal}
                  className="w-full bg-[#004B8D] hover:bg-[#003865]"
                  data-testid="accept-terms-button"
                >
                  {acceptingLegal ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept Terms & Privacy Policy
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2 - Document Upload (only enabled after legal acceptance) */}
        <Card className={`border-2 ${!hasAcceptedLegal ? 'border-slate-200 opacity-60' : 'border-slate-200'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${verification?.status === 'verified' ? 'bg-green-500' : hasAcceptedLegal ? 'bg-[#004B8D]' : 'bg-slate-400'}`}>
                {verification?.status === 'verified' ? <CheckCircle className="w-5 h-5" /> : '2'}
              </div>
              <div>
                <CardTitle className="text-lg">Identity Documents</CardTitle>
                <CardDescription>
                  {!hasAcceptedLegal ? 'Complete Step 1 first' : 'Upload your licence and take a selfie'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {verification?.status === 'rejected' && verification?.rejection_reason && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <p className="text-sm text-red-800">
                <strong>Rejection Reason:</strong> {verification.rejection_reason}
              </p>
              <p className="text-sm text-red-600 mt-2">Please re-upload your documents.</p>
            </CardContent>
          </Card>
        )}

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black z-50 flex flex-col">
            <div className="flex justify-between items-center p-4">
              <h2 className="text-white font-semibold">Take a Selfie</h2>
              <Button variant="ghost" size="icon" onClick={stopCamera} className="text-white">
                <X className="w-6 h-6" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted 
                className="max-w-full max-h-full"
              />
            </div>
            <div className="p-6 flex justify-center">
              <Button 
                onClick={capturePhoto}
                className="w-20 h-20 rounded-full bg-white hover:bg-slate-100"
              >
                <Camera className="w-8 h-8 text-slate-900" />
              </Button>
            </div>
          </div>
        )}

        {/* Documents - Only show if legal is accepted */}
        {hasAcceptedLegal && (
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="font-heading">Required Documents</CardTitle>
            <CardDescription>
              Upload clear photos of your documents and a selfie
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {documents.map((doc) => {
              const isUploaded = verification?.[doc.key];
              
              return (
                <div key={doc.key} className="p-3 sm:p-4 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isUploaded ? 'bg-green-100' : 'bg-slate-100'}`}>
                        {isUploaded ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <doc.icon className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{doc.label}</p>
                        <p className="text-xs text-slate-500">
                          {isUploaded ? 'Uploaded' : 'Not uploaded'}
                        </p>
                      </div>
                    </div>
                    
                    {doc.key === 'selfie' ? (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={startCamera}
                          disabled={uploading[doc.key]}
                        >
                          <Camera className="w-4 h-4 mr-1" />
                          Camera
                        </Button>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(doc.key, e.target.files[0])}
                            disabled={uploading[doc.key]}
                          />
                          <Button variant="outline" size="sm" asChild>
                            <span>
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </span>
                          </Button>
                        </label>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(doc.key, e.target.files[0])}
                          disabled={uploading[doc.key]}
                        />
                        <Button 
                          variant={isUploaded ? "outline" : "default"} 
                          size="sm"
                          className={!isUploaded ? "bg-[#004B8D] hover:bg-[#003865]" : ""}
                          asChild
                        >
                          <span>
                            {uploading[doc.key] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            {isUploaded ? 'Re-upload' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                  
                  {isUploaded && verification[`${doc.key}_url`] && (
                    <div className="mt-3">
                      <img 
                        src={verification[`${doc.key}_url`]}
                        alt={doc.label}
                        className="h-20 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(verification[`${doc.key}_url`], '_blank')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <p className="text-xs text-slate-500 mt-1">Click to view full image</p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
        )}

        {/* Helpful text - only show if legal accepted */}
        {hasAcceptedLegal && (
        <div className="text-center text-sm text-slate-500">
          <p>Your documents are reviewed by our team within 24 hours.</p>
          <p>We take your privacy seriously and handle all documents securely.</p>
        </div>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
